import React, { useEffect } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { 
  SettingsIcon,
  CustomersIcon,
  DollarIcon,
  TasksIcon,
  DashboardIcon,
  ConversationsIcon,
  LiveChatIcon
} from '../components/Icons'

interface SettingItem {
  title: string
  icon: React.ComponentType<{ className?: string }>
  href: string
  disabled?: boolean
}

interface SettingsSection {
  title: string
  items: SettingItem[]
}

const SettingsLayout: React.FC = () => {
  const { t } = useTranslation()
  const { isAdmin } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Redirect to profile by default
  useEffect(() => {
    if (location.pathname === '/dashboard/settings') {
      navigate('/dashboard/settings/profile', { replace: true })
    }
  }, [location.pathname, navigate])

  // Admin settings sections
  const adminSections: SettingsSection[] = [
    {
      title: t('settings.sections.account'),
      items: [
        {
          title: t('settings.profile'),
          icon: CustomersIcon,
          href: '/dashboard/settings/profile'
        }
      ]
    },
    {
      title: t('settings.sections.system'),
      items: [
        {
          title: t('settings.admin.systemConfig.title'),
          icon: SettingsIcon,
          href: '/dashboard/settings/system'
        },
        {
          title: t('settings.admin.platformSettings.title'),
          icon: DashboardIcon,
          href: '/dashboard/settings/platform',
          disabled: true
        }
      ]
    },
    {
      title: t('settings.sections.users'),
      items: [
        {
          title: t('settings.admin.userManagement.title'),
          icon: CustomersIcon,
          href: '/dashboard/users'
        },
        {
          title: t('settings.admin.customerMigration.title'),
          icon: CustomersIcon,
          href: '/dashboard/settings/customer-migration'
        }
      ]
    },
    {
      title: t('settings.sections.billing'),
      items: [
        {
          title: t('settings.admin.stripeConfig.title'),
          icon: DollarIcon,
          href: '/dashboard/settings/stripe'
        },
        {
          title: t('settings.admin.membershipManagement.title'),
          icon: CustomersIcon,
          href: '/dashboard/settings/memberships'
        },
        {
          title: t('settings.admin.billingManagement.title', 'Billing Management'),
          icon: DollarIcon,
          href: '/dashboard/settings/billing'
        }
      ]
    },
    {
      title: t('settings.sections.communication'),
      items: [
        {
          title: t('settings.admin.announcements.title'),
          icon: ConversationsIcon,
          href: '/dashboard/settings/announcements'
        },
        {
          title: t('settings.admin.smsAdminSettings.title'),
          icon: ConversationsIcon,
          href: '/dashboard/settings/admin/telnyx'
        },
        {
          title: t('settings.phoneNumberManagement.title'),
          icon: ConversationsIcon,
          href: '/dashboard/settings/admin/phone-numbers'
        },
        {
          title: t('settings.liveChat.title'),
          icon: ConversationsIcon,
          href: '/dashboard/settings/live-chat'
        },
        {
          title: t('settings.liveChatSettings.title'),
          icon: ConversationsIcon,
          href: '/dashboard/settings/chat'
        }
      ]
    },
    {
      title: t('settings.sections.analytics'),
      items: [
        {
          title: t('settings.admin.analytics.title'),
          icon: ConversationsIcon,
          href: '/dashboard/settings/analytics',
          disabled: true
        }
      ]
    }
  ]

  // Broker settings sections
  const brokerSections: SettingsSection[] = [
    {
      title: t('settings.sections.account'),
      items: [
        {
          title: t('settings.profile'),
          icon: CustomersIcon,
          href: '/dashboard/settings/profile'
        }
      ]
    },
    {
      title: t('settings.sections.deals'),
      items: [
        {
          title: t('settings.dealStatuses'),
          icon: DashboardIcon,
          href: '/dashboard/settings/deal-statuses'
        },
        {
          title: t('settings.dealTypes'),
          icon: DollarIcon,
          href: '/dashboard/settings/deal-types'
        },
        {
          title: t('settings.financialSettings'),
          icon: DollarIcon,
          href: '/dashboard/settings/financial'
        }
      ]
    },
    {
      title: t('settings.sections.tasks'),
      items: [
        {
          title: t('settings.taskManagement.title'),
          icon: TasksIcon,
          href: '/dashboard/settings/task-delays'
        },
        {
          title: t('settings.workflowManagement.title'),
          icon: DashboardIcon,
          href: '/dashboard/settings/workflows'
        }
      ]
    },
    {
      title: t('settings.sections.communication'),
      items: [
        {
          title: t('settings.smsSettings.title'),
          icon: ConversationsIcon,
          href: '/dashboard/settings/sms'
        },
        {
          title: t('settings.phoneNumberManagement.title'),
          icon: ConversationsIcon,
          href: '/dashboard/settings/phone-numbers'
        },
        {
          title: t('emailSettings.title'),
          icon: ConversationsIcon,
          href: '/dashboard/settings/email'
        },
        {
          title: t('settings.brokerSupport.title'),
          icon: ConversationsIcon,
          href: '/dashboard/support'
        }
      ]
    },
    {
      title: t('settings.sections.organization'),
      items: [
        {
          title: t('settings.membershipManagement.title'),
          icon: DollarIcon,
          href: '/dashboard/settings/membership'
        },
        {
          title: t('settings.usersManagement.title'),
          icon: CustomersIcon,
          href: '/dashboard/settings/users-management'
        }
      ]
    },
    {
      title: t('settings.sections.leads'),
      items: [
        {
          title: t('settings.leadsManagement.title'),
          icon: CustomersIcon,
          href: '/dashboard/settings/leads'
        },
        {
          title: t('settings.customFormSettings.title'),
          icon: TasksIcon,
          href: '/dashboard/settings/custom-forms'
        }
      ]
    },
    {
      title: t('settings.sections.schedule'),
      items: [
        {
          title: t('settings.scheduleSettings.title'),
          icon: TasksIcon,
          href: '/dashboard/settings/schedule'
        }
      ]
    }
  ]

  const sections = isAdmin ? adminSections : brokerSections

  return (
    <div className="flex h-screen">
      {/* Settings Sidebar */}
      <div className="w-64 bg-white dark:bg-gray-800 border-l-2 border-r-2 border-gray-200 dark:border-gray-700 overflow-y-auto flex-shrink-0">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('settings.title')}
          </h1>
        </div>

        {/* Navigation */}
        <nav className="px-3 pb-6 space-y-6">
          {sections.map((section) => (
            <div key={section.title}>
              <h3 className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const IconComponent = item.icon
                  
                  if (item.disabled) {
                    return (
                      <div
                        key={item.title}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-400 dark:text-gray-600 rounded-md cursor-not-allowed opacity-50"
                      >
                        <IconComponent className="h-5 w-5 mr-3" />
                        {item.title}
                        <span className="ml-auto text-xs">{t('settings.comingSoon')}</span>
                      </div>
                    )
                  }

                  return (
                    <NavLink
                      key={item.title}
                      to={item.href}
                      className={({ isActive }) =>
                        `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-gray-900 text-white dark:bg-gray-700'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <IconComponent className="h-5 w-5 mr-3" />
                      {item.title}
                    </NavLink>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  )
}

export default SettingsLayout

